'use client'; // Error boundaries must be Client Components

import type React from 'react';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';

import '@/app/globals.scss';
import { OnboardingLayout } from '@/features/onboarding/components/onboarding-layout';
import type { Locale, StaticTranslationString } from '@/types/types';
import { i18nConfig } from '@/types/types';
import { isDraftOrPreviewMode } from '@/utils/draft-mode';

const DRAFT_MODE_RELOAD_DELAY_MS = 2000;

const errorTitle: StaticTranslationString = {
  de: 'Es ist ein Fehler aufgetreten!',
  en: 'Something went wrong!',
  fr: "Une erreur s'est produite !",
};

const errorDescription: StaticTranslationString = {
  de: 'Es tut uns leid, aber es ist ein Fehler aufgetreten. Bitte versuche es erneut.',
  en: 'We are sorry, but something went wrong. Please try again.',
  fr: "Nous sommes désolés, une erreur s'est produite. Veuillez réessayer.",
};

const debugLogCopiedText: StaticTranslationString = {
  de: 'Fehlerprotokoll in Zwischenablage kopiert!',
  en: 'Error log copied to the clipboard!',
  fr: "Journal d'erreurs copié dans le presse-papiers !",
};

const retryText: StaticTranslationString = {
  de: 'Nochmals versuchen',
  en: 'Try again',
  fr: 'Réessayer',
};

const reconnectingText: StaticTranslationString = {
  de: 'Verbindung wird wiederhergestellt',
  en: 'Reconnecting',
  fr: 'Reconnexion en cours',
};

const unsubscribe = (): void => {};
// The cookie does not change while the error page is shown, so there is nothing to subscribe to.
const subscribeToNothing = (): (() => void) => unsubscribe;

/**
 * Reads the locale from its cookie. The root layout is gone when this page renders, so nothing
 * hands a locale down, and a router hook here would read the pathname in the prerendered
 * `/_global-error` page.
 */
const getLocaleFromDocumentCookie = (): Locale => {
  const cookieLocale = document.cookie
    .split('; ')
    .find((cookie) => cookie.startsWith(`${i18nConfig.localeCookie}=`))
    ?.split('=')[1];
  const enabledLocale = i18nConfig.locales.find((locale) => locale === cookieLocale);
  return (enabledLocale ?? i18nConfig.defaultLocale) as Locale;
};

const getServerLocale = (): Locale => i18nConfig.defaultLocale as Locale;

/**
 * This file is responsible for converting a general runtime error page.
 *
 * If this page happens, there was an uncaught error in the root layout of the app;
 * normally this should never happen.
 *
 * In draft/preview mode (Payload CMS Live Preview), transient network errors
 * are expected when the server briefly restarts. Instead of showing an error page
 * (which permanently breaks the iframe), we auto-reload after a short delay.
 *
 * @param error
 * @constructor
 */
const GlobalError: React.FC<{
  error: Error & { digest?: string };
}> = ({ error }) => {
  const [isRecovering, setIsRecovering] = useState(false);
  const [copiedDebug, setCopiedDebug] = useState(false);
  const clickTimestampsReference = useRef<number[]>([]);
  const locale = useSyncExternalStore(
    subscribeToNothing,
    getLocaleFromDocumentCookie,
    getServerLocale,
  );

  useEffect(() => {
    // Check if the error is likely due to being offline (e.g., failed to load a JS chunk)
    const isOfflineError =
      !navigator.onLine ||
      error.name === 'ChunkLoadError' ||
      error.message.toLowerCase().includes('failed to fetch') ||
      error.message.toLowerCase().includes('network error');

    // Completely disable global offline redirection for the Payload CMS admin panel.
    // Payload has its own offline handling and error boundaries.
    const isAdminPanel =
      typeof globalThis !== 'undefined' && globalThis.location.pathname.startsWith('/admin');

    // In draft/preview mode, auto-reload instead of showing the error page.
    // This prevents the Payload CMS Live Preview iframe from getting stuck.
    // We also do this for transient network errors in the admin panel to prevent the
    // root error boundary from permanently replacing the admin UI with a red error screen.
    const isTransientAdminError = isAdminPanel && isOfflineError;

    if (isDraftOrPreviewMode() || isTransientAdminError) {
      console.warn(
        '[GlobalError] Transient error in admin/preview mode. Auto-reloading in 2s:',
        error.message,
      );
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsRecovering(true);
      const timer = setTimeout((): void => {
        globalThis.location.reload();
      }, DRAFT_MODE_RELOAD_DELAY_MS);
      return (): void => {
        clearTimeout(timer);
      };
    }

    if (isOfflineError && !isAdminPanel) {
      console.error('[GlobalError] Network/Offline error detected:', {
        message: error.message,
        name: error.name,
        stack: error.stack,
        digest: error.digest,
        navigatorOnline: navigator.onLine,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      });
      console.log('[GlobalError] Redirecting to /~offline');
      globalThis.location.href = '/~offline';
      return;
    }

    console.error('Something went terribly wrong, we are sorry for that.');

    if (typeof globalThis !== 'undefined') {
      import('posthog-js')
        .then(({ default: posthog }) => {
          posthog.captureException(error);
        })
        .catch((error_: unknown) => console.error('Failed to capture error with PostHog', error_));
    }
    return;
  }, [error]);

  const handleTitleClick = (): void => {
    const now = Date.now();
    const recentClicks = [...clickTimestampsReference.current, now].filter(
      (timestamp) => now - timestamp < 2000,
    );
    clickTimestampsReference.current = recentClicks;

    if (recentClicks.length >= 5) {
      clickTimestampsReference.current = [];
      const currentUrl = globalThis.location.href;
      const userAgent = navigator.userAgent;
      const isOnline = navigator.onLine ? 'Online' : 'Offline';

      const debugInfo = [
        '--- GLOBAL ERROR DEBUG LOG ---',
        `Timestamp: ${new Date().toISOString()}`,
        `URL: ${currentUrl}`,
        `User Agent: ${userAgent}`,
        `Online Status: ${isOnline}`,
        `Error Name: ${error.name}`,
        `Error Message: ${error.message}`,
        `Error Digest: ${error.digest ?? 'N/A'}`,
        `Stack Trace:\n${error.stack ?? 'No stack trace available'}`,
      ].join('\n');

      void navigator.clipboard.writeText(debugInfo).then(() => {
        setCopiedDebug(true);
        setTimeout(() => {
          setCopiedDebug(false);
        }, 3000);
      });
    }
  };

  // In draft/preview mode, show a minimal recovery message instead of the full error page
  if (isRecovering) {
    return (
      <html lang={locale}>
        <body>
          <div className="flex h-dvh w-dvw flex-col items-center justify-center bg-gray-50 p-4">
            <p className="text-gray-500">{reconnectingText[locale]}</p>
          </div>
        </body>
      </html>
    );
  }

  return (
    <html lang={locale}>
      <body>
        <div className="flex h-dvh w-dvw flex-col items-center justify-center bg-gray-50 p-4">
          <OnboardingLayout>
            <h1
              className="text-conveniat-green mb-4 cursor-pointer text-xl font-bold select-none"
              onClick={handleTitleClick}
            >
              {errorTitle[locale]}
            </h1>
            <p className="mb-8 text-balance text-gray-700">{errorDescription[locale]}</p>
            {copiedDebug && (
              <div className="bg-conveniat-green/10 text-conveniat-green mb-4 rounded-md px-4 py-2 text-sm font-medium">
                {debugLogCopiedText[locale]}
              </div>
            )}
            <button
              className="font-heading bg-conveniat-green hover:bg-conveniat-green/90 transform cursor-pointer rounded-[8px] px-8 py-3 text-center text-lg leading-normal font-bold text-white shadow-md duration-100 hover:scale-[1.02] active:scale-[0.98]"
              onClick={() => globalThis.location.reload()}
            >
              {retryText[locale]}
            </button>
          </OnboardingLayout>
        </div>
      </body>
    </html>
  );
};

export default GlobalError;
